import {
  Button as AntButton,
  ButtonProps as AntButtonProps,
  theme,
} from 'antd';
import styled, { css } from 'styled-components';
import { ReactNode } from 'react';
import type { GlobalToken } from 'antd';
import {
  ComponentSize,
  getControlHeight,
  getFontSize,
  getGap,
  getIconSize,
  getPaddingHorizontal,
  getPaddingVertical,
} from '../../utils/tokenHelpers';
import { radius } from '../../theme/radius';

export type ButtonVariant =
  | 'primary'
  | 'outlined'
  | 'secondary'
  | 'tertiary'
  | 'utility'
  | 'outlinedAccent'
  | 'upgrade'
  | 'danger';

export interface ButtonProps extends Omit<
  AntButtonProps,
  'type' | 'size' | 'variant'
> {
  /** Button variant following Figma design system */
  variant?: ButtonVariant;
  /** Button size */
  size?: ComponentSize;
  /** Left icon */
  leftIcon?: ReactNode;
  /** Right icon */
  rightIcon?: ReactNode;
  /** Icon size in px; defaults to the `size` scale */
  iconSize?: number;
}

// Border radius config per size (from Figma)
const getBorderRadiusForButton = (
  token: GlobalToken,
  size: ComponentSize,
  variant: ButtonVariant,
): number => {
  // Utility variant has different border radius
  if (variant === 'utility') {
    const utilityRadius: Record<ComponentSize, number> = {
      xs: token.borderRadiusLG, // 16
      s: token.borderRadius, // 8
      m: token.borderRadius, // 8
      l: token.borderRadiusLG, // 16
    };
    return utilityRadius[size];
  }

  // Primary, secondary, tertiary are fully rounded (pill)
  return radius.pill;
};

// Variant styles using theme tokens
const getVariantStyles = (token: GlobalToken, variant: ButtonVariant) => {
  const variants = {
    primary: css`
      background: ${token.colorPrimary};
      color: ${token.colorWhite};
      border: none;

      &:hover:not(:disabled) {
        background: ${token.colorPrimaryHover} !important;
        color: ${token.colorWhite} !important;
      }

      &:active:not(:disabled) {
        background: ${token.colorPrimaryActive} !important;
        color: ${token.colorWhite} !important;
      }

      &:disabled {
        background: ${token.colorBgContainerDisabled};
        color: ${token.colorTextDisabled};
      }
    `,
    outlined: css`
      background: transparent;
      color: ${token.colorPrimary};
      border: 1px solid ${token.colorPrimary};

      &:hover:not(:disabled) {
        background: ${token.colorPrimaryBg} !important;
        color: ${token.colorPrimaryHover} !important;
        border-color: ${token.colorPrimaryHover} !important;
      }

      &:active:not(:disabled) {
        background: ${token.colorPrimaryBg} !important;
        color: ${token.colorPrimaryActive} !important;
        border-color: ${token.colorPrimaryActive} !important;
      }

      &:disabled {
        background: transparent;
        color: ${token.colorTextDisabled};
        border-color: ${token.colorBorder};
      }
    `,
    secondary: css`
      background: ${token.colorText};
      color: ${token.colorWhite};
      border: none;

      &:hover:not(:disabled) {
        background: ${token.colorTextSecondary} !important;
        color: ${token.colorWhite} !important;
      }

      &:active:not(:disabled) {
        background: ${token.colorTextBase} !important;
        color: ${token.colorWhite} !important;
      }

      &:disabled {
        background: ${token.colorBgContainerDisabled};
        color: ${token.colorTextDisabled};
      }
    `,
    tertiary: css`
      background: transparent;
      color: ${token.colorTextSecondary};
      border: none;
      box-shadow: none;

      &:hover:not(:disabled) {
        background: transparent !important;
        color: ${token.colorTextTertiary} !important;
      }

      &:active:not(:disabled) {
        background: transparent !important;
        color: ${token.colorText} !important;
      }

      &:disabled {
        background: transparent;
        color: ${token.colorTextDisabled};
      }
    `,
    utility: css`
      background: ${token.colorBgLayout};
      color: ${token.colorTextSecondary};
      border: none;

      &:hover:not(:disabled) {
        background: ${token.colorBorder} !important;
        color: ${token.colorTextSecondary} !important;
      }

      &:active:not(:disabled) {
        background: ${token.colorBorderSecondary} !important;
        color: ${token.colorWhite} !important;
      }

      &:disabled {
        background: ${token.colorBgContainerDisabled};
        color: ${token.colorTextDisabled};
      }
    `,
    // Accent-outlined pill on a tinted background; own geometry, like `upgrade`.
    outlinedAccent: css`
      height: auto;
      padding: ${token.paddingSM}px;
      border-radius: ${radius.pill}px;
      background: ${token.colorPrimaryBg};
      color: ${token.colorPrimary};
      border: 1px solid ${token.colorPrimary};

      &:hover:not(:disabled) {
        background: ${token.colorPrimaryBg} !important;
        color: ${token.colorPrimaryHover} !important;
        border-color: ${token.colorPrimaryHover} !important;
      }

      &:disabled {
        background: transparent;
        color: ${token.colorTextDisabled};
        border-color: ${token.colorBorder};
      }
    `,
    danger: css`
      background: ${token.colorError};
      color: ${token.colorWhite};
      border: none;

      &:hover:not(:disabled) {
        background: ${token.colorErrorActive} !important;
        color: ${token.colorWhite} !important;
      }

      &:active:not(:disabled) {
        background: ${token.colorErrorActive} !important;
        color: ${token.colorWhite} !important;
      }

      &:disabled {
        background: ${token.colorBgContainerDisabled};
        color: ${token.colorTextDisabled};
      }
    `,
    upgrade: css`
      background: linear-gradient(92deg, #6946d5 22.22%, #5423ec 80.77%);
      color: ${token.colorWhite};
      border: 2px solid ${token.colorPrimary};
      box-shadow: 0 3px 30px 0 rgba(0, 0, 0, 0.15);
      height: 40px;
      max-height: 40px;
      padding: 12px 24px 12px 18px;
      gap: 2px;
      font-size: 14px;
      font-weight: 500;
      line-height: 16px;
      letter-spacing: -0.14px;

      &:hover:not(:disabled) {
        background: linear-gradient(
          92deg,
          #5a38c6 22.22%,
          #4515dd 80.77%
        ) !important;
        color: ${token.colorWhite} !important;
        border-color: ${token.colorPrimary} !important;
      }

      &:active:not(:disabled) {
        background: linear-gradient(
          92deg,
          #4e2db8 22.22%,
          #3a0ece 80.77%
        ) !important;
        color: ${token.colorWhite} !important;
        border-color: ${token.colorPrimary} !important;
      }

      &:disabled {
        background: ${token.colorBgContainerDisabled};
        color: ${token.colorTextDisabled};
        border-color: ${token.colorBgContainerDisabled};
        box-shadow: none;
      }
    `,
  };
  return variants[variant];
};

interface StyledButtonProps {
  $token: GlobalToken;
  $variant: ButtonVariant;
  $height: number;
  $paddingH: number;
  $paddingV: number;
  $fontSize: number;
  $gap: number;
  $borderRadius: number;
}

const StyledButton = styled(AntButton)<StyledButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: ${({ $token }) => $token.fontFamily};
  line-height: 1;
  cursor: pointer;
  transition: all ${({ $token }) => $token.motionDurationMid} ease;

  /* Size styles from tokens */
  height: ${({ $height }) => $height}px;
  padding: ${({ $paddingV, $paddingH }) => `${$paddingV}px ${$paddingH}px`};
  gap: ${({ $gap }) => $gap}px;
  font-size: ${({ $fontSize }) => $fontSize}px;
  border-radius: ${({ $borderRadius }) => $borderRadius}px;

  /* Variant styles */
  ${({ $token, $variant }) => getVariantStyles($token, $variant)}

  &:disabled {
    cursor: not-allowed;
  }

  /* Override Ant Design's default focus styles */
  &:focus {
    outline: none;
  }

  /* Reset Ant Design's span wrapper */
  > span {
    display: inline-flex;
    align-items: center;
    gap: inherit;
  }
`;

interface IconWrapperProps {
  $iconSize: number;
}

const IconWrapper = styled.span<IconWrapperProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  svg {
    width: ${({ $iconSize }) => $iconSize}px;
    height: ${({ $iconSize }) => $iconSize}px;
  }
`;

/**
 * Button component based on Figma Design System
 *
 * Now uses Ant Design theme tokens for consistent styling.
 * Supports four sizes (xs, s, m, l) and four variants (primary, secondary, tertiary, utility).
 *
 * @example
 * ```tsx
 * import { Button } from '@libertify/ui';
 *
 * // Basic usage
 * <Button variant="primary" size="m">Click me</Button>
 *
 * // With icons
 * <Button variant="secondary" size="l" leftIcon={<PlusIcon />}>Add item</Button>
 *
 * // Different sizes
 * <Button variant="primary" size="xs">Extra Small</Button>
 * <Button variant="primary" size="s">Small</Button>
 * <Button variant="primary" size="m">Medium</Button>
 * <Button variant="primary" size="l">Large</Button>
 *
 * // Utility button
 * <Button variant="utility" size="s">Filter</Button>
 * ```
 */
export const Button = ({
  variant = 'primary',
  size = 'm',
  leftIcon,
  rightIcon,
  iconSize,
  children,
  ...props
}: ButtonProps) => {
  const { token } = theme.useToken();

  const height = getControlHeight(token, size);
  const paddingH = getPaddingHorizontal(token, size);
  const paddingV = getPaddingVertical(token, size);
  const fontSize = getFontSize(token, size);
  const gap = getGap(token, size);
  const resolvedIconSize = iconSize ?? getIconSize(token, size);
  const borderRadius = getBorderRadiusForButton(token, size, variant);

  return (
    <StyledButton
      $token={token}
      $variant={variant}
      $height={height}
      $paddingH={paddingH}
      $paddingV={paddingV}
      $fontSize={fontSize}
      $gap={gap}
      $borderRadius={borderRadius}
      {...props}
    >
      {leftIcon && <IconWrapper $iconSize={resolvedIconSize}>{leftIcon}</IconWrapper>}
      {children}
      {rightIcon && <IconWrapper $iconSize={resolvedIconSize}>{rightIcon}</IconWrapper>}
    </StyledButton>
  );
};

export default Button;
